import type { ComparativeResultsType, CLTResultType, PJNecessarioType, AnaliseType, CalculoBreakdown, FeriasBreakdown } from '../calculadora-comparativa';
import { CONSTANTES_2025 } from '../calculadora-comparativa';

// Tabela INSS 2025
const TABELA_INSS_2025 = [
  { limite: 1518.0, aliquota: 0.075, parcelaDeducao: 0 },
  { limite: 2793.88, aliquota: 0.09, parcelaDeducao: 22.77 },
  { limite: 4190.83, aliquota: 0.12, parcelaDeducao: 106.59 },
  { limite: 8157.41, aliquota: 0.14, parcelaDeducao: 190.4 }
] as const;

// TABELA IRRF 2025
const TABELA_IRRF_2025 = [
  { limite: 2428.80, aliquota: 0, parcelaDeducao: 0 },           
  { limite: 2826.65, aliquota: 0.075, parcelaDeducao: 182.16 }, 
  { limite: 3751.05, aliquota: 0.15, parcelaDeducao: 394.16 },  
  { limite: 4664.68, aliquota: 0.225, parcelaDeducao: 675.49 }, 
  { limite: Number.POSITIVE_INFINITY, aliquota: 0.275, parcelaDeducao: 908.73 } 
] as const;

const TABELA_PLR_2025 = [
  { limite: 7640.80, aliquota: 0, parcelaDeducao: 0 },           
  { limite: 9922.28, aliquota: 0.075, parcelaDeducao: 573.06 }, 
  { limite: 13167.00, aliquota: 0.15, parcelaDeducao: 1317.23 },  
  { limite: 16380.38, aliquota: 0.225, parcelaDeducao: 2304.76 }, 
  { limite: Number.POSITIVE_INFINITY, aliquota: 0.275, parcelaDeducao: 3123.78 } 
] as const;

const REGIMES_PJ = {
  mei: {
    nome: 'MEI',
    aliquota: 0.065,
    limiteMensal: 6750.00,
    limitAnual: 81000.00
  },
  simples: {
    nome: 'Simples Nacional',
    aliquota: 0.093, 
    limitAnual: 4800000.00
  },
  presumido: {
    nome: 'Lucro Presumido',
    aliquota: 0.163, 
    limitAnual: Infinity
  }
} as const;

const parseNumericValue = (value: string): number => {
  if (!value || typeof value !== 'string') return 0;
  return Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0;
};

const parseIntegerValue = (value: string): number => {
  const numero = Number.parseInt(value) || 0;
  return Math.max(0, numero);
};

const arredondarMoeda = (valor: number): number => Math.round(valor * 100) / 100;


/**
 * INSS - CÁLCULO PROGRESSIVO POR FAIXAS
 */
export const calcularINSS = (salario: number): number => {
  if (salario <= 0) return 0;

  const baseCalculo = Math.min(salario, CONSTANTES_2025.TETO_INSS);
  let totalINSS = 0;
  let limiteAnterior = 0;

  for (const faixa of TABELA_INSS_2025) {
    if (baseCalculo > limiteAnterior) {
      const valorFaixa = Math.min(baseCalculo, faixa.limite) - limiteAnterior;
      if (valorFaixa > 0) {
        totalINSS += valorFaixa * faixa.aliquota;
      }
      limiteAnterior = faixa.limite;
    }
  }

  return Math.max(0, totalINSS);
};

/**
 * BREAKDOWN INSS
 */
export const calcularINSSBreakdown = (salario: number): CalculoBreakdown => {
  const baseCalculo = Math.min(salario, CONSTANTES_2025.TETO_INSS);
  const faixas = [];
  let valorTotal = 0;
  let limiteAnterior = 0;

  for (const faixa of TABELA_INSS_2025) {
    if (baseCalculo > limiteAnterior) {
      const valorFaixa = Math.min(baseCalculo, faixa.limite) - limiteAnterior;
      const contribuicaoFaixa = valorFaixa * faixa.aliquota;
      
      if (valorFaixa > 0) {
        faixas.push({
          limite: faixa.limite,
          aliquota: faixa.aliquota * 100,
          valorFaixa,
          contribuicaoFaixa,
          limiteInicial: limiteAnterior,
          limiteFinal: Math.min(baseCalculo, faixa.limite)
        });
        valorTotal += contribuicaoFaixa;
      }
      limiteAnterior = faixa.limite;
    }
  }

  return {
    faixas,
    valorTotal,
    baseCalculo
  };
};

/**
 * IRRF
 */
export const calcularIRRF = (salario: number, inss: number, dependentes: number): number => {
  const baseCalculo = Math.max(0, salario - inss - dependentes * CONSTANTES_2025.VALOR_DEPENDENTE_IRRF);

  if (baseCalculo <= 0) return 0;

  let faixa = null;
  for (const item of TABELA_IRRF_2025) {
    if (baseCalculo <= item.limite) {
      faixa = item;
      break;
    }
  }

  if (!faixa) {
    faixa = TABELA_IRRF_2025[TABELA_IRRF_2025.length - 1];
  }

  const valorIRRF = baseCalculo * faixa.aliquota - faixa.parcelaDeducao;
  return Math.max(0, valorIRRF);
};

/**
 * BREAKDOWN IRRF 
 */
export const calcularIRRFBreakdown = (salario: number, inss: number, dependentes: number) => {
  const deducaoDependentes = dependentes * CONSTANTES_2025.VALOR_DEPENDENTE_IRRF;
  const baseCalculo = Math.max(0, salario - inss - deducaoDependentes);
  
  let faixaAplicada = null;
  for (const faixa of TABELA_IRRF_2025) {
    if (baseCalculo <= faixa.limite) {
      faixaAplicada = faixa;
      break;
    }
  }

  if (!faixaAplicada) {
    faixaAplicada = TABELA_IRRF_2025[TABELA_IRRF_2025.length - 1];
  }

  const valorIRRF = Math.max(0, baseCalculo * faixaAplicada.aliquota - faixaAplicada.parcelaDeducao);

  return {
    salario,
    inss,
    dependentes,
    deducaoDependentes,
    baseCalculo,
    faixaAplicada,
    valorIRRF
  };
};

/**
 * Salário-família 
 */
export const verificarSalarioFamilia = (salarioBruto: number, dependentesMenores14: number): number => {
  if (dependentesMenores14 > 0 && salarioBruto <= CONSTANTES_2025.TETO_SALARIO_FAMILIA) {
    return dependentesMenores14 * CONSTANTES_2025.SALARIO_FAMILIA;
  }
  return 0;
};

/**
 * PLR
 */
const calcularPLRLiquido = (valorPLR: number): number => {
  if (valorPLR <= 0) return 0;

  let faixa = null;
  for (const item of TABELA_PLR_2025) {
    if (valorPLR <= item.limite) {
      faixa = item;
      break;
    }
  }

  if (!faixa) {
    faixa = TABELA_PLR_2025[TABELA_PLR_2025.length - 1];
  }

  const valorIRRF = valorPLR * faixa.aliquota - faixa.parcelaDeducao;
  const imposto = Math.max(0, valorIRRF);
  
  return valorPLR - imposto;
};

/**
 * FÉRIAS - CÁLCULO E BREAKDOWN
 */
export const calcularFeriasCompleto = (salarioBruto: number, dependentes: number): FeriasBreakdown => {
  const salarioFerias = salarioBruto;
  const umTercoFerias = salarioBruto / 3;
  const brutoFerias = salarioFerias + umTercoFerias;
  
  const inssFerias = calcularINSS(brutoFerias);
  const irrfFerias = calcularIRRF(brutoFerias, inssFerias, dependentes);
  const salarioFamiliaFerias = verificarSalarioFamilia(salarioBruto, 0); 
  
  const feriasLiquidas = brutoFerias - inssFerias - irrfFerias + salarioFamiliaFerias;

  return {
    salarioFerias,
    umTercoFerias,
    brutoFerias,
    inssFerias,
    irrfFerias,
    feriasLiquidas
  };
};

/**
 * FGTS mês a mês
 */
const calcularFGTSAnual = (salarioBruto: number): number => {
  let fgtsTotal = 0;
  
  for (let mes = 1; mes <= 12; mes++) {
    fgtsTotal += salarioBruto * CONSTANTES_2025.FGTS_ALIQUOTA;
  }
  
  fgtsTotal += salarioBruto * CONSTANTES_2025.FGTS_ALIQUOTA;
  
  const brutoFerias = salarioBruto + (salarioBruto / 3);
  fgtsTotal += brutoFerias * CONSTANTES_2025.FGTS_ALIQUOTA;
  
  return fgtsTotal;
};

/**
 * Calcula o total anual CLT (com PLR)
 */
export const calcularTotalCLTAnual = (
  salarioBruto: number,
  numeroDependentes: number,
  dependentesMenores14: number,
  valeAlimentacao: number,
  valeTransporte: number,
  planoSaude: number,
  outrosBeneficios: number,
  plr: number = 0
): CLTResultType => {

  const inss = calcularINSS(salarioBruto);
  const irrf = calcularIRRF(salarioBruto, inss, numeroDependentes);
  const salarioFamilia = verificarSalarioFamilia(salarioBruto, dependentesMenores14);
  const salarioLiquido = salarioBruto - inss - irrf + salarioFamilia;

  const salarios12x = salarioLiquido * 12;

  const inss13 = calcularINSS(salarioBruto);
  const irrf13 = calcularIRRF(salarioBruto, inss13, numeroDependentes);
  const salarioFamilia13 = verificarSalarioFamilia(salarioBruto, dependentesMenores14);
  const decimoTerceiro = salarioBruto - inss13 - irrf13 + salarioFamilia13;

  const feriasCompleto = calcularFeriasCompleto(salarioBruto, numeroDependentes);
  const ferias = feriasCompleto.feriasLiquidas;

  const fgts = calcularFGTSAnual(salarioBruto);
  const plrLiquido = calcularPLRLiquido(plr);
  const beneficiosAnuais = (valeAlimentacao + valeTransporte + planoSaude + outrosBeneficios) * 12;

  const totalAnual = salarios12x + decimoTerceiro + ferias + fgts + beneficiosAnuais + plrLiquido;
  const equivalenciaMensal = totalAnual / 12;

  return {
    salarioBruto: arredondarMoeda(salarioBruto),
    salarioLiquido: arredondarMoeda(salarioLiquido),
    inss: arredondarMoeda(inss),
    irrf: arredondarMoeda(irrf),
    salarioFamilia: arredondarMoeda(salarioFamilia),
    salarios12x: arredondarMoeda(salarios12x),
    decimoTerceiro: arredondarMoeda(decimoTerceiro),
    ferias: arredondarMoeda(ferias),
    fgts: arredondarMoeda(fgts),
    plr: arredondarMoeda(plrLiquido),
    beneficiosAnuais: arredondarMoeda(beneficiosAnuais),
    totalAnual: arredondarMoeda(totalAnual),
    equivalenciaMensal: arredondarMoeda(equivalenciaMensal),
  };
};

/**
 * Calcula PJ necessário 
 */
export const calcularPJNecessario = (
  equivalenciaMensal: number, 
  regimePJ: 'mei' | 'simples' | 'presumido',
  salarioBrutoCLT: number
): PJNecessarioType => {
  const regime = REGIMES_PJ[regimePJ];
  
  // Validação MEI
  if (regimePJ === 'mei') {
    const faturamentoAnualNecessario = equivalenciaMensal * 12;
    if (faturamentoAnualNecessario > regime.limitAnual) {
      return {
        erro: true,
        mensagem: `Faturamento anual necessário (R$ ${Math.round(faturamentoAnualNecessario).toLocaleString('pt-BR')}) excede limite MEI (R$ 81.000)`,
        regime: regime.nome,
        minimo: 0,
        recomendado: 0,
        impostos: 0,
        aliquota: 0,
        margemPercent: 0,
        proLabore: 0,
        inssProLabore: 0,
        irrfProLabore: 0,
      };
    }
    
    if ('limiteMensal' in regime && equivalenciaMensal > regime.limiteMensal) {
      return {
        erro: true,
        mensagem: `Equivalência mensal (R$ ${equivalenciaMensal.toFixed(2).replace('.', ',')}) excede limite MEI mensal (R$ ${regime.limiteMensal.toFixed(2).replace('.', ',')})`,
        regime: regime.nome,
        minimo: 0,
        recomendado: 0,
        impostos: 0,
        aliquota: 0,
        margemPercent: 0,
        proLabore: 0,
        inssProLabore: 0,
        irrfProLabore: 0,
      };
    }
  }

  // Pró-labore estimado
  let proLaboreEstimado = 0;
  let inssProLabore = 0;
  let irrfProLabore = 0;
  
  if (regimePJ !== 'mei') {
    proLaboreEstimado = Math.max(
      CONSTANTES_2025.SALARIO_MINIMO,
      Math.min(
        salarioBrutoCLT * 0.35,
        CONSTANTES_2025.TETO_INSS
      )
    );
    
    inssProLabore = calcularINSS(proLaboreEstimado);
    irrfProLabore = calcularIRRF(proLaboreEstimado, inssProLabore, 0);
  }
  
  const proLaboreLiquido = proLaboreEstimado - inssProLabore - irrfProLabore;
  const distribuicaoNecessaria = Math.max(0, equivalenciaMensal - proLaboreLiquido);
  const faturamentoMinimo = (proLaboreEstimado + distribuicaoNecessaria) / (1 - regime.aliquota);
  const margemSeguranca = faturamentoMinimo * 1.20;
  const impostosTotal = margemSeguranca * regime.aliquota;

  return {
    erro: false,
    regime: regime.nome,
    minimo: arredondarMoeda(faturamentoMinimo),
    recomendado: arredondarMoeda(margemSeguranca),
    impostos: arredondarMoeda(impostosTotal),
    aliquota: arredondarMoeda(regime.aliquota * 100),
    margemPercent: 20,
    proLabore: arredondarMoeda(proLaboreEstimado),
    inssProLabore: arredondarMoeda(inssProLabore),
    irrfProLabore: arredondarMoeda(irrfProLabore),
  };
};

/**
 * Análise qualitativa
 */
export const gerarAnalise = (cltResult: CLTResultType, pjNecessario: PJNecessarioType): AnaliseType => {
  const vantagens = [
    "Maior flexibilidade de horários e local de trabalho",
    "Possibilidade de atender múltiplos clientes",
    "Potencial de ganhos superiores ao CLT",
    "Menos burocracias trabalhistas",
    "Distribuição de lucros sem tributação PF (até R$ 20.000/mês)",
    "Controle total sobre a gestão do negócio"
  ];

  const desvantagens = [
    "Sem estabilidade trabalhista (demissão sem justa causa)",
    "Sem direito a seguro-desemprego",
    "Precisa custear próprios benefícios (plano saúde, etc.)",
    "Responsabilidade total por gestão fiscal e contábil",
    "Renda variável conforme demanda do mercado",
    "Sem férias remuneradas obrigatórias"
  ];

  let recomendacao = "";
  
  if (pjNecessario.erro) {
    recomendacao = "Para esta faixa salarial, o MEI não é adequado. Considere o regime Simples Nacional que permite faturamento maior e oferece mais flexibilidade.";
  } else {
    const diferenca = ((pjNecessario.recomendado / cltResult.equivalenciaMensal - 1) * 100);
    
    if (diferenca <= 15) {
      recomendacao = "A migração para PJ é muito vantajosa! A diferença é pequena e você ganha flexibilidade e autonomia. Certifique-se de ter demanda garantida.";
    } else if (diferenca <= 30) {
      recomendacao = "PJ pode ser vantajoso se você valoriza flexibilidade e tem clientes garantidos. A diferença é moderada e compensável.";
    } else if (diferenca <= 50) {
      recomendacao = "PJ exige ganho moderadamente maior que CLT. Avalie sua capacidade de conseguir e manter clientes antes de migrar.";
    } else {
      recomendacao = "PJ exige ganho significativamente maior que CLT. Recomendamos manter o regime CLT por ora, a não ser que tenha demanda muito garantida.";
    }
    
    if (cltResult.salarioFamilia > 0) {
      recomendacao += ` Atenção: Como PJ, você perderia o salário-família de R$ ${(cltResult.salarioFamilia * 12).toFixed(2).replace('.', ',')} anuais.`;
    }
    
    if (cltResult.plr > 0) {
      recomendacao += ` Considere que a PLR de R$ ${cltResult.plr.toFixed(2).replace('.', ',')} não existe no regime PJ.`;
    }
    
    if (cltResult.beneficiosAnuais > 3000) {
      recomendacao += ` Considere o custo de reposição dos benefícios empresariais (R$ ${cltResult.beneficiosAnuais.toFixed(2).replace('.', ',')} anuais).`;
    }
  }

  return { vantagens, desvantagens, recomendacao };
};

/**
 * Validação com salário mínimo
 */
export const validarParametrosComparativo = (
  salarioBruto: number,
  numeroDependentes: number,
  dependentesMenores14: number
): void => {
  if (salarioBruto < CONSTANTES_2025.SALARIO_MINIMO) {
    throw new Error(`Salário deve ser pelo menos R$ ${CONSTANTES_2025.SALARIO_MINIMO.toFixed(2).replace('.', ',')} (salário mínimo)`);
  }
  
  if (numeroDependentes < 0) {
    throw new Error("Número de dependentes não pode ser negativo");
  }
  
  if (dependentesMenores14 < 0) {
    throw new Error("Dependentes menores de 14 anos não pode ser negativo");
  }
  
  if (dependentesMenores14 > numeroDependentes) {
    throw new Error("Dependentes menores de 14 anos não pode ser maior que total de dependentes");
  }
};

/**
 * Função principal
 */
export const calcularComparativo = (
  salarioBruto: string,
  numeroDependentes: string,
  dependentesMenores14: string,
  valeAlimentacao: string,
  valeTransporte: string,
  planoSaude: string,
  outrosBeneficios: string,
  plr: string,
  regimePJ: 'mei' | 'simples' | 'presumido'
): ComparativeResultsType => {

  const salarioBrutoNum = parseNumericValue(salarioBruto);
  const dependentesNum = parseIntegerValue(numeroDependentes);
  const dependentesMenores14Num = parseIntegerValue(dependentesMenores14);
  const valeAlimentacaoNum = parseNumericValue(valeAlimentacao);
  const valeTransporteNum = parseNumericValue(valeTransporte);
  const planoSaudeNum = parseNumericValue(planoSaude);
  const outrosBeneficiosNum = parseNumericValue(outrosBeneficios);
  const plrNum = parseNumericValue(plr);

  validarParametrosComparativo(salarioBrutoNum, dependentesNum, dependentesMenores14Num);

  // Cálculo do total CLT
  const cltResult = calcularTotalCLTAnual(
    salarioBrutoNum,
    dependentesNum,
    dependentesMenores14Num,
    valeAlimentacaoNum,
    valeTransporteNum,
    planoSaudeNum,
    outrosBeneficiosNum,
    plrNum
  );

  // Cálculo PJ necessário
  const pjNecessario = calcularPJNecessario(
    cltResult.equivalenciaMensal, 
    regimePJ,
    salarioBrutoNum
  );
  
  const diferenciaMensal = pjNecessario.erro ? 0 : pjNecessario.recomendado - cltResult.equivalenciaMensal;
  const percentualDiferenca = pjNecessario.erro ? 0 : ((pjNecessario.recomendado / cltResult.equivalenciaMensal - 1) * 100);
  
  const analise = gerarAnalise(cltResult, pjNecessario);

  return {
    cltResult,
    pjNecessario,
    diferenciaMensal: arredondarMoeda(diferenciaMensal),
    percentualDiferenca: arredondarMoeda(percentualDiferenca),
    analise,
  };
};